import { useCallback } from 'react';
const globalState = new Map<
  string,
  {
    promise: Promise<any[]> | null;
    timer: NodeJS.Timeout | null;
    lastTrigger: number;
  }
>();

interface CacheItem<T> {
  data: T[];
  expiry: number;
}

const memoryCache = new Map<string, CacheItem<any>>();

const DEBOUNCE_MS = 300;
const DEFAULT_TTL_MINUTES = 10;

export const useCachedList = <T, Result extends Record<string, any>>(
  cacheKey: string,
  useLazyFetchHook: () => { execute: () => Promise<Result | undefined> },
  dataKey: keyof Result,
  ttlMinutes = DEFAULT_TTL_MINUTES
) => {
  const { execute } = useLazyFetchHook();

  const getList = useCallback(
    async (id?: string, forceRefresh = false): Promise<T[]> => {
      const now = Date.now();
      const cached = memoryCache.get(cacheKey);

      if (!forceRefresh && cached && cached.expiry > now) {
        const items = cached.data as T[];
        if (id) {
          const found = items.find((item: any) => item.id === id);
          if (found) return [found];
        }
        return items;
      }

      if (id && cached) {
        const found = cached.data.find((item: any) => item.id === id);
        if (found) return [found];
      }

      const state = globalState.get(cacheKey) || {
        promise: null,
        timer: null,
        lastTrigger: 0,
      };
      const timeSinceLast = now - state.lastTrigger;

      if (state.promise) {
        return state.promise.then((data) => {
          if (id) {
            const found = data.find((item: any) => item.id === id);
            return found ? [found] : [];
          }
          return data;
        });
      }

      const trigger = () => {
        const promise = execute().then((result) => {
          if (!result || !(dataKey in result)) {
            const empty: T[] = [];
            memoryCache.set(cacheKey, {
              data: empty,
              expiry: now + ttlMinutes * 60 * 1000,
            });
            globalState.set(cacheKey, { ...state, promise: null, timer: null });
            return empty;
          }

          const data = (result as any)[dataKey] as T[];
          memoryCache.set(cacheKey, {
            data,
            expiry: Date.now() + ttlMinutes * 60 * 1000,
          });

          // Clean up
          globalState.set(cacheKey, { ...state, promise: null, timer: null });
          return data;
        });

        globalState.set(cacheKey, { ...state, promise });
        return promise;
      };

      let promise: Promise<T[]>;

      if (timeSinceLast > DEBOUNCE_MS) {
        promise = trigger();
      } else {
        if (state.timer) clearTimeout(state.timer);

        const timer = setTimeout(() => {
          globalState.set(cacheKey, {
            ...state,
            timer: null,
            lastTrigger: Date.now(),
          });
          trigger();
        }, DEBOUNCE_MS - timeSinceLast);

        globalState.set(cacheKey, { ...state, timer, lastTrigger: now });
        promise = new Promise((resolve) => {
          const check = () => {
            const current = globalState.get(cacheKey);
            if (current?.promise) {
              current.promise.then(resolve);
            } else {
              setTimeout(check, 10);
            }
          };
          check();
        });
      }

      const result = await promise;

      if (id) {
        const found = result.find((item: any) => item.id === id);
        return found ? [found] : [];
      }

      return result;
    },
    [cacheKey, execute, dataKey, ttlMinutes]
  );

  const invalidateCache = useCallback(() => {
    memoryCache.delete(cacheKey);
    const state = globalState.get(cacheKey);

    if (state?.timer) {
      clearTimeout(state.timer);
    }

    globalState.delete(cacheKey);
  }, [cacheKey]);

  const isCached = useCallback(() => {
    const entry = memoryCache.get(cacheKey);
    return !!(entry && entry.expiry > Date.now());
  }, [cacheKey]);

  return { getList, invalidateCache, isCached };
};

export default useCachedList;
